import { faArchive, faCloudUpload, faHome, faImage, faLink, faPaperclip, faPlus, faSave } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useContext } from "react"
import { AuthenticationContext } from "../../AuthenticationContext"
import FabButton from "../common/FabButton"
import { CustomerDetail } from "./customerApi"
import { getPrimaryContacts } from "./customersModel"
import "./CustomerHero.css"

type Props = {
  customer: CustomerDetail
  isDeletedItem: boolean
  dirty: boolean
  isSaving: boolean
  primaryPhotoUrl?: string | null
  onNew: () => void
  onWorkTab: () => void
  onAttachmentsTab: () => void
  onOpenPhotoGallery: () => void
  onUpload: () => void
  onQbo: () => void
  onSave: () => void
}

const CustomerHero = ({
  customer,
  isDeletedItem,
  dirty,
  isSaving,
  primaryPhotoUrl,
  onNew,
  onWorkTab,
  onAttachmentsTab,
  onOpenPhotoGallery,
  onUpload,
  onQbo,
  onSave,
}: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const canAccessAccounting = currentUser?.roles.includes('Accounting') ?? false
  const isConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks ?? false
  const showQbo = canAccessAccounting && isConnectedToQuickBooks

  const heroPhotoUrl = primaryPhotoUrl ?? customer.primaryPhotoUrl
  const isNew = customer.id === 0

  const primaryPhones = getPrimaryContacts(customer.phones) ?? []
  const primaryEmails = getPrimaryContacts(customer.emails) ?? []

  const qboTitle = customer.qboId
    ? `Linked in QuickBooks Online to Customer ${customer.qboDisplayName || `# ${customer.qboId}`}`
    : 'Link Customer in QuickBooks Online'

  return <>
    <div className="hero-container short customer-hero">
      <div
        className="customer-hero__photo"
        style={heroPhotoUrl ? { backgroundImage: `url(${heroPhotoUrl})` } : undefined}
      />
    </div>

    <div className="hero-details">
      <h1>
        {customer.displayName}
        <br />
        <small className="primary-phones">
          {primaryPhones.map((phone, i) => <a key={`p${i}`} href={`tel:${phone.number}`}>{phone.number}</a>)}
        </small>
        <small className="primary-emails">
          {primaryEmails.map((email, i) => <a key={`e${i}`} className="email" href={`mailto:${email.address}`}>{email.address}</a>)}
        </small>
      </h1>

      {isDeletedItem && <h1 className="hero-details-deleted-item">
        <span>
          <span className="margin-40">DELETED</span>
          <br />
          <small className="display-none-tiny">(scroll to bottom and click on "Restore" to un-delete it)</small>
        </span>
      </h1>}

      {!isDeletedItem && <div className="fab-buttons-container">
        <button id="btn-add-new" type="button" onClick={onNew} disabled={isNew}>
          <div className="oval" />
          <span><FontAwesomeIcon icon={faPlus} />New</span>
        </button>
        <FabButton
          color="orange"
          icon={faHome}
          title="Properties"
          href={`#!/customers/${customer.url}/properties`}
          badge={customer.properties}
          disabled={isNew}
        />
        <FabButton
          color="orange"
          icon={faArchive}
          title="Work"
          badge={customer.projects}
          disabled={isNew}
          onClick={onWorkTab}
        />
        <FabButton
          color="blue"
          icon={faImage}
          title="Select Primary Photo"
          disabled={isNew || customer.attachments === 0}
          onClick={onOpenPhotoGallery}
        />
        <FabButton
          color="blue"
          icon={faPaperclip}
          title="Attachments"
          badge={customer.attachments}
          disabled={isNew}
          onClick={onAttachmentsTab}
        />
        <FabButton
          color="blue"
          icon={faCloudUpload}
          title="Upload Attachment"
          disabled={isNew}
          onClick={onUpload}
        />
        {showQbo && <FabButton
          color={customer.qboId ? 'blue' : 'orange'}
          icon={faLink}
          title={qboTitle}
          disabled={isNew}
          onClick={onQbo}
        />}
        <FabButton icon={faSave} title="Save Changes" disabled={isSaving || !dirty} onClick={onSave} />
      </div>}
    </div>
  </>
}

export default CustomerHero
